/**
 * Email bodies for the Messages card. New messages are stored as plain text with line breaks;
 * messages imported from the old system have "<br />" for line breaks, or HTML squashed onto one line.
 */

const ENTITIES: Record<string, string> = { nbsp: " ", amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" };

/** Plain text with line breaks, from either kind of stored body. */
export function emailPlainText(body: string): string {
  return body
    .replace(/\r\n?/g, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr)>/gi, "\n")
    .replace(/<[a-z!/][^>]*>/gi, "")
    .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (all, e: string) => {
      const k = e.toLowerCase();
      if (k.startsWith("#x")) return String.fromCodePoint(parseInt(k.slice(2), 16));
      if (k.startsWith("#")) return String.fromCodePoint(Number(k.slice(1)));
      return ENTITIES[k] ?? all;
    })
    .replace(/[ \t ]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// Where the quoted history of a reply starts. Each pattern's match starts at the history.
const QUOTE_STARTS: RegExp[] = [
  // Gmail, Apple Mail, most phones: "On Mon, Nov 2, 2026 at 3:12 PM WatchRepairPros.com <support@…> wrote:"
  // (may wrap onto a second line, or be squashed onto one line in old imported messages)
  /(?<=^|\s)On\s(?:[^\n]|\n(?!\n)){4,300}?\swrote:/,
  // Spanish: "El mar, 3 de nov de 2026, 11:07 a.m., WatchRepairPros.com <…> escribió:"
  /(?<=^|\s)El\s(?:[^\n]|\n(?!\n)){4,300}?\sescribió:/,
  // Outlook
  /(?<=^|\n)[ \t]*-{2,}\s*Original Message\s*-{2,}/i,
  /(?<=^|\n)[ \t]*_{10,}\s*\n\s*From:/,
  /(?<=^|\s)From:\s[^\n]{1,200}?\s(?:Sent|Date):\s/,
  // Plain-text quoting
  /(?<=^|\n)>/,
];

/** Splits an email into what the customer wrote and the earlier messages quoted below it. */
export function splitQuoted(text: string): { main: string; earlier: string } {
  let at = -1;
  for (const re of QUOTE_STARTS) {
    const m = re.exec(text);
    if (m && (at < 0 || m.index < at)) at = m.index;
  }
  if (at < 0) return { main: text, earlier: "" };
  const main = text.slice(0, at).trim();
  const earlier = text.slice(at).trim();
  return main ? { main, earlier } : { main: text, earlier: "" };
}

/** One line for the ticket list: what the customer wrote, without quoted history. */
export function messagePreview(body: string | null): string | null {
  if (body == null) return null;
  return splitQuoted(emailPlainText(body)).main.replace(/\s+/g, " ").trim().slice(0, 200);
}

/** Quoted history for reading: without the ">" markers and runs of empty quoted lines. */
export function readableQuote(earlier: string): string {
  return earlier.replace(/^[ \t]*(?:>[ \t]?)+/gm, "").replace(/\n{3,}/g, "\n\n").trim();
}
